import {
    AuditLogChangeType,
    HistoryDetailsItem,
    RefValue,
} from "@common/types/auditLog";
import { TransactionRuleCondition } from "@common/types/transactionRule";
import { isRecord } from "../../utils/historyDetailsHelpers";
import { isConditionField } from "../views/historyRuleDetailsViewConfig";
import { extractRuleDataFromSnapshot } from "./ruleSnapshotAdapter";

function castFromResource(raw: unknown): TransactionRuleCondition[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw
        .filter(
            (item): item is Record<string, unknown> =>
                !!item && typeof item === "object" && "conditionType" in item,
        )
        .map((c, i) => ({
            id: String(c.id ?? `cond-${i}`),
            conditionType: c.conditionType,
            value: c.value,
            transferDirection: c.transferDirection ?? null,
        })) as TransactionRuleCondition[];
}

function isRefValue(item: unknown): item is RefValue {
    return !!item && typeof item === "object" && "value" in item;
}

function toArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

function formatRefValue(ref: RefValue): string {
    if (ref.valueLabel) {
        return ref.valueLabel;
    }
    const v = ref.value;
    if (typeof v === "string" || typeof v === "number") {
        return String(v);
    }
    if (v && typeof v === "object" && ("min" in v || "max" in v)) {
        const amount = v as { min?: number; max?: number };
        if (amount.min !== undefined && amount.max !== undefined) {
            return `${amount.min} – ${amount.max}`;
        }
        if (amount.min !== undefined) {
            return `≥ ${amount.min}`;
        }
        if (amount.max !== undefined) {
            return `≤ ${amount.max}`;
        }
    }
    return JSON.stringify(v);
}

function refValuesToText(refs: RefValue[]): string {
    return refs.map(formatRefValue).join(", ");
}

interface ListRowConditions {
    conditions: TransactionRuleCondition[];
    conditionTexts: string[];
}

function extractConditionTypeFromField(field: string): string | null {
    if (!isConditionField(field)) {
        return null;
    }
    const conditionType = field.slice("condition:".length);
    return conditionType.length > 0 ? conditionType : null;
}

function extractPerFieldStructuredConditions(
    log: HistoryDetailsItem,
): TransactionRuleCondition[] {
    const side =
        log.changeType === AuditLogChangeType.DELETE ? "before" : "after";

    return log.diff
        .filter((d) => isConditionField(d.field))
        .map((d) => {
            const raw = d[side].value;
            if (!isRecord(raw)) {
                return null;
            }

            const conditionType =
                raw.conditionType ?? extractConditionTypeFromField(d.field);
            if (!conditionType) {
                return null;
            }

            return {
                id: d.field,
                conditionType,
                value: raw.value,
                transferDirection: raw.transferDirection ?? null,
            } as TransactionRuleCondition;
        })
        .filter(
            (condition): condition is TransactionRuleCondition =>
                condition !== null,
        );
}

function extractPerFieldConditionTexts(log: HistoryDetailsItem): string[] {
    const side =
        log.changeType === AuditLogChangeType.DELETE ? "before" : "after";
    return log.diff
        .filter((d) => isConditionField(d.field))
        .map((d) => {
            const raw = d[side].value;
            const refs = isRefValue(raw)
                ? toArray(raw.value).filter(isRefValue)
                : toArray(raw).filter(isRefValue);
            return refs.length > 0 ? refValuesToText(refs) : null;
        })
        .filter((t): t is string => t !== null);
}

function getPreferredSnapshotFromMetadata(
    log: HistoryDetailsItem,
): Record<string, unknown> | undefined {
    const metadata = log.metadata;
    if (!isRecord(metadata)) {
        return undefined;
    }

    const beforeSnapshot = isRecord(metadata.beforeSnapshot)
        ? metadata.beforeSnapshot
        : undefined;
    const afterSnapshot = isRecord(metadata.afterSnapshot)
        ? metadata.afterSnapshot
        : undefined;

    if (log.changeType === AuditLogChangeType.DELETE) {
        return beforeSnapshot ?? afterSnapshot;
    }
    return afterSnapshot ?? beforeSnapshot;
}

export function extractConditionsForListRow(
    log: HistoryDetailsItem,
): ListRowConditions {
    const fromResource = castFromResource(log.resource?.conditions);
    if (fromResource.length > 0) {
        return { conditions: fromResource, conditionTexts: [] };
    }

    const metadataSnapshot = getPreferredSnapshotFromMetadata(log);
    if (metadataSnapshot) {
        const fromMetadata = extractRuleDataFromSnapshot(
            metadataSnapshot,
            log.resource,
        );
        if (fromMetadata.conditions.length > 0) {
            return { conditions: fromMetadata.conditions, conditionTexts: [] };
        }
    }

    const perFieldStructured = extractPerFieldStructuredConditions(log);
    if (perFieldStructured.length > 0) {
        return { conditions: perFieldStructured, conditionTexts: [] };
    }

    const perFieldTexts = extractPerFieldConditionTexts(log);
    if (perFieldTexts.length > 0) {
        return { conditions: [], conditionTexts: perFieldTexts };
    }

    const rawDiff = log.diff.find((d) => d.field === "rawConditions");
    const condDiff = log.diff.find((d) => d.field === "conditions");
    const diff = rawDiff ?? condDiff;
    if (!diff) {
        return { conditions: [], conditionTexts: [] };
    }
    const raw =
        log.changeType === AuditLogChangeType.DELETE
            ? diff.before.value
            : diff.after.value;
    if (!Array.isArray(raw)) {
        return { conditions: [], conditionTexts: [] };
    }

    const structured = raw
        .filter(
            (item): item is Record<string, unknown> =>
                !!item && typeof item === "object" && "conditionType" in item,
        )
        .map((c, i) => ({
            id: String(c.id ?? `cond-${i}`),
            conditionType: c.conditionType,
            value: c.value,
            transferDirection: c.transferDirection ?? null,
        })) as TransactionRuleCondition[];

    if (structured.length > 0) {
        return { conditions: structured, conditionTexts: [] };
    }

    const texts = raw.filter(
        (item): item is string => typeof item === "string",
    );
    return { conditions: [], conditionTexts: texts };
}
